/** Pure money helpers for Member 360 KPIs (Agent-UI book parity). */

import type { MemberAccount } from '@/features/member/memberAccount'

export function round2(n: number): number {
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 100 + Number.EPSILON) / 100
}

export function limitUsage(exposure: number, creditLimit: number): number {
  if (!(creditLimit > 0)) return exposure > 0 ? 1 : 0
  return exposure / creditLimit
}

/** Live balance less open exposure — Free to Give / Available Credit. */
export function availableToGive(balance: number, exposure: number): number {
  return Math.max(0, round2(balance - exposure))
}

export type BalanceLine = {
  id: string
  label: string
  value: number
  /** Hide for non-players when Lab gate was player-only. */
  playerOnly?: boolean
}

/**
 * Lab Balance tab rows for the selected member (Agent `balanceLinesFromAccount`).
 * Product NE from Agent-API player get. Premium BM omitted (not in Strix yet).
 * Horse racing stays 0 until racebook schema exists.
 */
export function balanceLinesFromAccount(account: MemberAccount): BalanceLine[] {
  const free = availableToGive(account.balance, account.exposure)
  const sports =
    account.exposureSportsbook !== undefined ? account.exposureSportsbook : account.exposure
  const lines: BalanceLine[] = [
    { id: 'ne-sportsbook', label: 'Net Exposure (Sports Book)', value: sports },
    {
      id: 'ne-cards',
      label: 'Net Exposure (Live Cards)',
      value: account.exposureLiveCards ?? 0,
      playerOnly: true,
    },
    {
      id: 'ne-casino',
      label: 'Net Exposure (Live Casino)',
      value: account.exposureLiveCasino ?? 0,
      playerOnly: true,
    },
    {
      id: 'ne-racing',
      label: 'Net Exposure (Horse Racing)',
      value: account.exposureHorseRacing ?? 0,
      playerOnly: true,
    },
    { id: 'balance-up', label: 'Balance Up', value: Math.max(0, account.settlement) },
    { id: 'balance-down', label: 'Balance Down', value: Math.max(0, -account.settlement) },
    { id: 'credit-limit', label: 'Credit Limit', value: account.creditLimit },
    { id: 'available', label: 'Available Credit', value: free },
  ]
  if (account.role !== 'player') {
    lines.push({
      id: 'distributed',
      label: 'Total credit given to downline',
      value: account.distributedBalance,
    })
  }
  return lines.filter((line) => !line.playerOnly || account.role === 'player')
}
